import React, { createContext, useContext, useEffect, useState } from 'react';

import { isEqualWith } from 'lodash-es';
import { and, debounce, propEq } from 'utils';

interface Dimensions {
  innerHeight: number;
  innerWidth: number;
}

const dims = ({ innerHeight, innerWidth }: Dimensions) => ({ innerHeight, innerWidth });

export const WindowSizeContext = createContext<Dimensions>(dims(window));
export const useWindowSize = () => useContext(WindowSizeContext);

const sameHeight = propEq<Dimensions, 'innerHeight'>('innerHeight');
const sameWidth = propEq<Dimensions, 'innerWidth'>('innerWidth');
const sameDimensions = and(sameHeight, sameWidth);

const dimensionsHaveChanged = (prev: Dimensions, next: Dimensions) =>
  !isEqualWith(prev, next, (objValue, othValue) => sameDimensions(objValue, othValue));

export const WindowSizeProvider: React.FC = ({ children }) => {
  const [dimensions, setDimensions] = useState(dims(window));
  useEffect(() => {
    const handleResize = debounce(() => {
      if (dimensionsHaveChanged(dimensions, window)) {
        setDimensions(dims(window));
      }
    }, 100);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [dimensions]);
  return <WindowSizeContext.Provider value={dimensions}>{children}</WindowSizeContext.Provider>;
};

export default WindowSizeContext.Consumer;
